/*********************************************************************************
 * This is `select` from ngrx/store pulled out into a pipeable operator with the *
 * the type definitions fixed as per https://github.com/ngrx/platform/issues/528 *
 ********************************************************************************/

import { Observable } from 'rxjs/Observable';
import { distinctUntilChanged, map, pluck } from 'rxjs/operators';
import { pipe } from 'rxjs/util/pipe';

export function select<T, K>(
  mapFn: (state: T) => K,
): ((source: Observable<T>) => Observable<K | undefined>);
export function select<T, a extends keyof T>(
  key: a,
): ((source: Observable<T>) => Observable<T[a] | undefined>);
export function select<T, a extends keyof T, b extends keyof T[a]>(
  key1: a,
  key2: b,
): ((source: Observable<T>) => Observable<T[a][b] | undefined>);
export function select<
  T,
  a extends keyof T,
  b extends keyof T[a],
  c extends keyof T[a][b]
>(
  key1: a,
  key2: b,
  key3: c,
): ((source: Observable<T>) => Observable<T[a][b][c] | undefined>);
export function select<
  T,
  a extends keyof T,
  b extends keyof T[a],
  c extends keyof T[a][b],
  d extends keyof T[a][b][c]
>(
  key1: a,
  key2: b,
  key3: c,
  key4: d,
): ((source: Observable<T>) => Observable<T[a][b][c][d] | undefined>);
export function select<
  T,
  a extends keyof T,
  b extends keyof T[a],
  c extends keyof T[a][b],
  d extends keyof T[a][b][c],
  e extends keyof T[a][b][c][d]
>(
  key1: a,
  key2: b,
  key3: c,
  key4: d,
  key5: e,
): ((source: Observable<T>) => Observable<T[a][b][c][d][e] | undefined>);
export function select<
  T,
  a extends keyof T,
  b extends keyof T[a],
  c extends keyof T[a][b],
  d extends keyof T[a][b][c],
  e extends keyof T[a][b][c][d],
  f extends keyof T[a][b][c][d][e]
>(
  key1: a,
  key2: b,
  key3: c,
  key4: d,
  key5: e,
  key6: f,
): ((source: Observable<T>) => Observable<T[a][b][c][d][e][f] | undefined>);
export function select<T>(
  pathOrMapFn: ((state: T) => any) | string,
  ...paths: string[]
): ((source: Observable<T>) => Observable<any>) {
  if (typeof pathOrMapFn === 'string') {
    return pipe(pluck<T, any>(pathOrMapFn, ...paths), distinctUntilChanged());
  } else if (typeof pathOrMapFn === 'function') {
    return pipe(map(pathOrMapFn), distinctUntilChanged());
  }

  throw new TypeError(
    `Unexpected type '${typeof pathOrMapFn}' in select operator,` +
      ` expected 'string' or 'function'`,
  );
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/operators/select.ts